'use client';

import { useState, useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn, getInitials } from '@/lib/utils';
import { TennisBallIcon, getBallLevelColor } from '@/components/ui/tennis-ball-icon';
import { useSubmitEvaluation } from '@/hooks/use-evaluation';
import { EVALUATION_CRITERIA as CRITERIA } from '@/shared/constants/evaluation';

interface PlayerToEvaluate {
  id: string;
  firstName: string;
  lastName: string;
  level: string | null;
  ballLevel?: string | null;
}

interface BulkEvaluationWizardProps {
  sessionId: string;
  players: PlayerToEvaluate[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
}

interface PlayerEvaluation {
  playerId: string;
  attitude: number;
  effort: number;
  engagement: number;
  physical: number;
  technical: number;
  tactical: number;
  submitted: boolean;
}

export function BulkEvaluationWizard({
  sessionId,
  players,
  open,
  onOpenChange,
  onComplete,
}: BulkEvaluationWizardProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [evaluations, setEvaluations] = useState<Record<string, PlayerEvaluation>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize evaluations when dialog opens with players
  useEffect(() => {
    if (open && players.length > 0) {
      setEvaluations(prev => {
        // Check if we already have evaluations for these players
        const hasAllPlayers = players.every(p => prev[p.id]);
        if (hasAllPlayers) return prev;

        // Initialize fresh evaluations
        const initial: Record<string, PlayerEvaluation> = {};
        players.forEach(p => {
          initial[p.id] = {
            playerId: p.id,
            attitude: 0,
            effort: 0,
            engagement: 0,
            physical: 0,
            technical: 0,
            tactical: 0,
            submitted: false,
          };
        });
        return initial;
      });
      setCurrentIndex(0);
    }
  }, [open, players]);

  const submitEvaluation = useSubmitEvaluation();

  const currentPlayer = players[currentIndex];
  const currentEval = currentPlayer ? evaluations[currentPlayer.id] : null;
  const isCurrentComplete = currentEval
    ? currentEval.attitude > 0 && currentEval.effort > 0 && currentEval.engagement > 0 &&
      currentEval.physical > 0 && currentEval.technical > 0 && currentEval.tactical > 0
    : false;

  const completedCount = Object.values(evaluations).filter(e => e.submitted).length;
  const totalCount = players.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const handleScoreChange = useCallback((criterion: string, value: number) => {
    if (!currentPlayer) return;
    setEvaluations(prev => ({
      ...prev,
      [currentPlayer.id]: {
        ...prev[currentPlayer.id],
        [criterion]: value,
      },
    }));
  }, [currentPlayer]);

  const handleSubmitCurrent = async () => {
    if (!currentPlayer || !isCurrentComplete) return;

    const eval_ = evaluations[currentPlayer.id];
    try {
      await submitEvaluation.mutateAsync({
        playerId: currentPlayer.id,
        sessionId,
        attitude: eval_.attitude,
        effort: eval_.effort,
        engagement: eval_.engagement,
        physical: eval_.physical,
        technical: eval_.technical,
        tactical: eval_.tactical,
      });

      setEvaluations(prev => ({
        ...prev,
        [currentPlayer.id]: {
          ...prev[currentPlayer.id],
          submitted: true,
        },
      }));

      // Auto-advance to next if not last
      if (currentIndex < players.length - 1) {
        setCurrentIndex(currentIndex + 1);
      }
    } catch (error) {
      console.error('Failed to submit evaluation:', error);
    }
  };

  const handleNext = () => {
    if (currentIndex < players.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleFinish = async () => {
    setIsSubmitting(true);

    // Submit any remaining evaluations that are complete but not submitted
    const pendingEvals = Object.values(evaluations).filter(
      e => !e.submitted && e.attitude > 0 && e.effort > 0 && e.engagement > 0 &&
           e.physical > 0 && e.technical > 0 && e.tactical > 0
    );

    try {
      await Promise.all(
        pendingEvals.map(eval_ =>
          submitEvaluation.mutateAsync({
            playerId: eval_.playerId,
            sessionId,
            attitude: eval_.attitude,
            effort: eval_.effort,
            engagement: eval_.engagement,
            physical: eval_.physical,
            technical: eval_.technical,
            tactical: eval_.tactical,
          })
        )
      );
    } catch (error) {
      console.error('Failed to submit remaining evaluations:', error);
    }

    setIsSubmitting(false);
    onComplete();
    onOpenChange(false);
  };

  const handleClose = () => {
    setCurrentIndex(0);
    onOpenChange(false);
  };

  if (players.length === 0) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Quick Evaluations</DialogTitle>
        </DialogHeader>

        {currentPlayer && currentEval && (
          <div className="flex-1 overflow-y-auto space-y-3">
            {/* Progress */}
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Progress</span>
                <span className="font-medium">{completedCount}/{totalCount} completed</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Player indicator */}
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePrevious}
                disabled={currentIndex === 0}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Prev
              </Button>
              <span className="text-sm text-muted-foreground">
                {currentIndex + 1} of {totalCount}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNext}
                disabled={currentIndex === players.length - 1}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {/* Current Player */}
            <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-clay-400 to-clay-600 flex items-center justify-center text-white font-bold text-sm">
                {getInitials(currentPlayer.firstName, currentPlayer.lastName)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-foreground truncate">
                  {currentPlayer.firstName} {currentPlayer.lastName}
                </div>
                <div className="text-xs text-muted-foreground capitalize flex items-center gap-1.5">
                  {currentPlayer.level || 'Unassigned'}
                  {(() => {
                    const ball = getBallLevelColor(currentPlayer.ballLevel);
                    return ball ? (
                      <TennisBallIcon color={ball.color} size={14} title={`${ball.label} Ball`} />
                    ) : null;
                  })()}
                </div>
              </div>
              {currentEval.submitted && (
                <div className="flex items-center gap-1 text-green-600">
                  <Check className="w-4 h-4" />
                  <span className="text-xs font-medium">Done</span>
                </div>
              )}
            </div>

            {/* 6-Tap Evaluation - Compact 2-column grid */}
            <div className="grid grid-cols-2 gap-2">
              {CRITERIA.map(({ key, label }) => {
                const score = currentEval[key] as number;
                return (
                  <div key={key} className="space-y-1">
                    <div className="flex justify-between items-center">
                      <label className="text-sm font-medium text-foreground">{label}</label>
                      {score > 0 && (
                        <span className="text-xs text-primary font-medium">
                          {score}/4
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4].map(rating => (
                        <button
                          key={rating}
                          type="button"
                          onClick={() => handleScoreChange(key, rating)}
                          disabled={currentEval.submitted}
                          className={cn(
                            'flex-1 py-2 rounded font-medium text-sm transition-all',
                            score === rating
                              ? 'bg-primary text-primary-foreground shadow-sm'
                              : currentEval.submitted
                              ? 'bg-muted text-muted-foreground'
                              : 'bg-muted text-muted-foreground hover:bg-muted/80'
                          )}
                        >
                          {rating}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              {!currentEval.submitted && (
                <Button
                  onClick={handleSubmitCurrent}
                  disabled={!isCurrentComplete || submitEvaluation.isPending}
                  className="flex-1"
                >
                  {submitEvaluation.isPending ? 'Saving...' : 'Save & Next'}
                </Button>
              )}
              <Button
                variant={currentEval.submitted ? 'default' : 'outline'}
                onClick={handleFinish}
                disabled={isSubmitting}
                className={currentEval.submitted ? 'flex-1' : ''}
              >
                {isSubmitting ? 'Finishing...' : 'Finish'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
